/* eslint-disable react/no-unescaped-entities */
/**
 * FreedrIQ Tutorial — deep-dive walkthrough of the FreedrIQ assessment
 * system. Covers what FreedrIQ measures, the four tiers, pre vs post
 * phases, the Community Challenge, growth reports, and personalised
 * study plans.
 */
import React from 'react';
import ToolSelector from '../../components/ToolSelector';
import TutorialLayout, { Callout, StepList, TryItButton } from '../../components/TutorialLayout';

const sections = [
  {
    id: 'overview',
    title: 'What is FreedrIQ?',
    body: (
      <>
        <p>
          <strong>FreedrIQ</strong> is the operator-grade skills assessment inside
          Freeze Dry Academy. Think of it as your freeze-drying <em>fitness score</em>
          {' '}— a combination of a <strong>Confidence baseline</strong> (how sure you feel)
          and a <strong>FreezeDryIQ</strong> technical score (what you actually know),
          measured before and after each course you finish.
        </p>
        <p>Every FreedrIQ session gives you three things:</p>
        <ul>
          <li>A tier-appropriate technical score out of 100.</li>
          <li>A confidence read from a short self-assessment.</li>
          <li>A <strong>personalised study plan</strong> pointing at the lessons you should tackle next.</li>
        </ul>
        <Callout kind="tip">
          FreedrIQ is <strong>not</strong> a certification. It's a diagnostic so you know
          where to focus. You can retake it any time.
        </Callout>
        <TryItButton to="/freedriq?tier=free&phase=pre" label="Take your Free FreedrIQ baseline" />
      </>
    ),
  },
  {
    id: 'tiers',
    title: 'The four tiers — Free, Pro, Business, Enterprise',
    body: (
      <>
        <p>Each tier targets a different operator profile and question depth:</p>
        <ul>
          <li>
            <strong>Free (25 questions)</strong> — foundational cycle science and safety.
            Anyone can take it. Great for hobbyists starting out.
          </li>
          <li>
            <strong>Pro (25 questions)</strong> — production-grade batch math, moisture
            control, and equipment troubleshooting. Requires a Pro membership or a
            <strong> Pro Access</strong> code.
          </li>
          <li>
            <strong>Business (35 questions)</strong> — pricing, unit economics, capacity
            planning, and margin analysis for freeze-dry businesses. Requires a
            <strong> Business Access</strong> code.
          </li>
          <li>
            <strong>Enterprise (40 questions)</strong> — commercial-scale planning,
            regulatory compliance, warehousing, and multi-machine operations. Enterprise
            tier only.
          </li>
        </ul>
        <Callout kind="info">
          Total question bank: <strong>125 questions</strong> across the four tiers,
          all written and vetted by working operators.
        </Callout>
      </>
    ),
  },
  {
    id: 'pre-post',
    title: 'Pre vs Post — the growth loop',
    body: (
      <>
        <p>
          Every tier has two phases: <strong>Pre-learning</strong> (before you take the
          course) and <strong>Post-learning</strong> (after you finish it). The difference
          is your growth report.
        </p>
        <StepList
          steps={[
            'Take FreedrIQ (pre) at the start of a tier — it establishes your baseline.',
            'Work through the tier\u2019s courses in the Learning catalog.',
            'Take FreedrIQ (post) once you\u2019ve completed the tier.',
            'Open your Growth Report to see side-by-side pre vs post scores, question-by-question shifts, and the topics that moved the most.',
          ]}
        />
        <Callout kind="tip">
          Pre-learning results also seed your <strong>onboarding survey</strong> and let
          Prof. FreedrIQ personalise recommendations. Skip pre, and you'll get generic
          suggestions.
        </Callout>
        <TryItButton to="/freedriq/growth" label="See your growth report" />
      </>
    ),
  },
  {
    id: 'study-plan',
    title: 'Your personalised study plan',
    body: (
      <>
        <p>
          Every FreedrIQ submission returns a <strong>study plan</strong> — a ranked
          list of the lessons you should tackle next, based on the questions you got
          wrong and the topics you flagged as low-confidence.
        </p>
        <p>The plan surfaces in three places:</p>
        <ul>
          <li>Immediately at the end of a FreedrIQ session (with "Start now" buttons on each lesson).</li>
          <li>On your Dashboard as a <strong>StudyPlanCard</strong> in the feed column.</li>
          <li>Inside the Learning catalog — flagged lessons show a small "Recommended for you" badge.</li>
        </ul>
        <Callout kind="info">
          Complete a study-plan lesson and it silently drops off the list. Retake FreedrIQ
          (post) to regenerate the plan against your new baseline.
        </Callout>
      </>
    ),
  },
  {
    id: 'challenge',
    title: 'FreedrIQ Community Challenge',
    body: (
      <>
        <p>
          The Community Challenge is a monthly, all-tiers-open <strong>timed
          gauntlet</strong> — 100 questions drawn from every FreedrIQ tier — with a
          global leaderboard and a personal rank card.
        </p>
        <ul>
          <li><strong>Top 10 leaderboard</strong> refreshes live as new attempts submit.</li>
          <li>Your personal card shows your rank, percentile, and best score.</li>
          <li>Every attempt generates a <strong>shareable OG image</strong> you can post
              on LinkedIn / Instagram / X to show off your rank.</li>
        </ul>
        <TryItButton to="/freedriq/challenge" label="Enter this month\u2019s challenge" />
        <Callout kind="tip">
          Challenge scores don't affect your tier progression — they're a
          leaderboard-only vanity metric, but a great way to benchmark yourself
          against the community.
        </Callout>
      </>
    ),
  },
  {
    id: 'auto-translation',
    title: 'Multi-language & auto-translation',
    body: (
      <>
        <p>
          FreedrIQ ships in <strong>five languages</strong> (English, Spanish, French,
          German, Portuguese). Switch languages via the globe icon in the top-right,
          and every question, answer, and study-plan card is auto-translated in real
          time using our LLM pipeline.
        </p>
        <Callout kind="info">
          Auto-translation is <em>cached</em> — the second time you (or anyone) reads
          the same question in Spanish, it's near-instant. Zero LLM cost, zero wait.
        </Callout>
      </>
    ),
  },
  {
    id: 'retakes-privacy',
    title: 'Retakes, privacy, and cheating',
    body: (
      <>
        <p>
          You can retake FreedrIQ as many times as you want. Every attempt is stored
          under your account, and only your <strong>most recent</strong> score is
          used for your dashboard tile and study plan.
        </p>
        <ul>
          <li>
            <strong>Privacy:</strong> individual attempts are private. Nobody except
            admins can see your raw answers. The Community Challenge only exposes your
            aggregate score and rank.
          </li>
          <li>
            <strong>Cheating:</strong> we don't run keystroke-timing forensics on
            FreedrIQ (it's a diagnostic, not a cert). But the Community Challenge
            does track submission duration — extreme outliers are flagged.
          </li>
        </ul>
        <Callout kind="tip">
          Struggling with a specific tier? Post in the <strong>Community forum</strong> —
          many operators walk through their pre → post journey publicly.
        </Callout>
      </>
    ),
  },
];

export default function TutorialFreedrIQ() {
  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <TutorialLayout
        eyebrow="Tutorial"
        title="FreedrIQ — the skills diagnostic"
        subtitle="Everything you need to make the most of FreedrIQ — from your first Free baseline to the Community Challenge. 7 sections, ~8 minute read."
        sections={sections}
        backTo="/freedriq"
        backLabel="Back to FreedrIQ"
      />
    </div>
  );
}
